import { useMemo, useState } from "react";
import { LockOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type { TablePaginationConfig } from "antd/es/table/interface";
import clsx from "clsx";
import { GridViewIcon, ListViewIcon } from "@/shared/assets/svg/icons";
import { InputSelect, type IStatus } from "@shared/ui";
import { INTEGRATION_STATUS_FILTER_OPTIONS } from "../../model/integration-status-filter-options";
import { MOCK_JOURNAL_VERSIONS } from "../../model/mock-data";
import type { JournalVersion } from "../../model/types";
import { VersionCards } from "../version-cards";
import { VersionTable } from "../version-table";
import "./journal-versions-list-panel.scss";

type ViewMode = "table" | "cards";

type JournalVersionsListPanelProps = {
  versions?: JournalVersion[];
  onVersionRowClick?: (version: JournalVersion) => void;
  responsibleColumnTitle?: string;
  versionDateColumnTitle?: string;
  showChangeReasonColumn?: boolean;
  tablePagination?: false | TablePaginationConfig;
  accessDenied?: boolean;
  accessDeniedMessage?: string;
};

export const JournalVersionsListPanel = ({
  versions: versionsProp,
  onVersionRowClick,
  responsibleColumnTitle,
  versionDateColumnTitle,
  showChangeReasonColumn,
  tablePagination = false,
  accessDenied = false,
  accessDeniedMessage = "У вас нет прав на просмотр списка версий",
}: JournalVersionsListPanelProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [statusFilter, setStatusFilter] = useState<IStatus | null>(null);

  const sourceVersions = versionsProp ?? MOCK_JOURNAL_VERSIONS;

  const filteredVersions = useMemo(() => {
    if (!statusFilter) {
      return sourceVersions;
    }
    if (statusFilter.id === "NONE") {
      return sourceVersions.filter((v) => v.integrationStatus === null);
    }
    return sourceVersions.filter(
      (v) => v.integrationStatus === statusFilter.id,
    );
  }, [statusFilter, sourceVersions]);

  if (accessDenied) {
    return (
      <div className="journal-versions-list-panel">
        <div className="journal-versions-list-panel__access-denied">
          <LockOutlined className="journal-versions-list-panel__access-denied-icon" />
          <p className="journal-versions-list-panel__access-denied-text">
            {accessDeniedMessage}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="journal-versions-list-panel">
      <div className="journal-versions-list-panel__toolbar">
        <div className="journal-versions-list-panel__filters">
          <div className="journal-versions-list-panel__filters-label">
            Фильтрация
          </div>
          <InputSelect
            title="Статус"
            style={{ marginRight: "8px" }}
            statuses={INTEGRATION_STATUS_FILTER_OPTIONS}
            selectedStatus={statusFilter}
            setSelectedStatus={setStatusFilter}
          />
        </div>

        <div className="journal-versions-list-panel__view-toggle">
          <Button
            type="text"
            icon={<GridViewIcon />}
            className={clsx("journal-versions-list-panel__toggle-btn", viewMode === "cards" && "journal-versions-list-panel__toggle-btn--active")}
            onClick={() => setViewMode("cards")}
          />
          <Button
            type="text"
            icon={<ListViewIcon />}
            className={clsx("journal-versions-list-panel__toggle-btn", viewMode === "table" && "journal-versions-list-panel__toggle-btn--active")}
            onClick={() => setViewMode("table")}
          />
        </div>
      </div>

      <div className="journal-versions-list-panel__content">
        {viewMode === "table" ? (
          <VersionTable
            versions={filteredVersions}
            onVersionClick={onVersionRowClick}
            responsibleColumnTitle={responsibleColumnTitle}
            versionDateColumnTitle={versionDateColumnTitle}
            showChangeReasonColumn={showChangeReasonColumn}
            pagination={
              tablePagination === false
                ? false
                : {
                    ...tablePagination,
                    total: filteredVersions.length,
                  }
            }
          />
        ) : (
          <VersionCards
            versions={filteredVersions}
            onVersionClick={onVersionRowClick}
          />
        )}
      </div>
    </div>
  );
};
